'use client';

import { useSelectedLayoutSegments } from 'next/navigation';
import type { ReactNode } from 'react';

import BaseDrawer from '@commons/components/Drawer';
import { useRouter } from '@commons/i18n/navigation';

// Type definition of the InterceptedRouteDrawer component properties.

type Props = {
  children: ReactNode;
};

/**
 * Drawer client component.
 *
 * Renders an open drawer that triggers a back navigation when closed.
 * Used with parallel and intercepted routes to have deep-linked drawers.
 *
 * @see https://nextjs.org/docs/app/building-your-application/routing/parallel-routes#modals
 */

const Drawer = ({ children }: Props) => {
  const { back } = useRouter();
  const segments = useSelectedLayoutSegments();

  // Early return if the route is not intercepted.
  if (segments.length < 1 || !segments[0].startsWith('(.)')) {
    return null;
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setTimeout(() => {
        back();
      }, 300);
    }
  };

  return (
    <BaseDrawer
      width={{ xs: 12, md: 11, lg: 10, xl: 8 }}
      defaultOpen
      onOpenChange={handleOpenChange}
    >
      {children}
    </BaseDrawer>
  );
};

export default Drawer;
